import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api'; // Import API service instead of axios
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import debounce from 'lodash.debounce'; // Import debounce from lodash
import axios from 'axios';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom modern marker with pulse effect
const customMarkerIcon = L.divIcon({
  className: 'bloomnav-marker',
  html: '<div class="bm-pulse"></div><div class="bm-dot"></div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15]
});

// Component to handle map events and marker drag
function LocationMarker({ position, setPosition, updateAddress }) {
  const map = useMapEvents({
    click: (e) => {
      setPosition(e.latlng);
      updateAddress(e.latlng);
      map.flyTo(e.latlng, map.getZoom());
    },
  });

  return position === null ? null : (
    <Marker 
      position={position} 
      draggable={true}
      icon={customMarkerIcon}
      eventHandlers={{
        dragend: (e) => {
          const marker = e.target;
          const position = marker.getLatLng();
          setPosition(position);
          updateAddress(position);
        },
      }}
    >
      <Popup>You are here</Popup>
    </Marker>
  );
}

// Component to display company details with better formatting
const CompanyDetails = ({ company }) => {
  if (!company) {
    return null;
  }

  return (
    <div className="company-details-card">
      <h4 className="company-name">{company.name || 'Company Name Unavailable'}</h4>
      <div className="company-info-container">
        {company.address && (
          <div className="info-item">
            <span className="info-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/></svg>
            </span>
            <span className="info-text">{company.address.address}</span>
          </div>
        )}
        {company.email && (
          <div className="info-item">
            <span className="info-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
            </span>
            <span className="info-text">
              <a href={`mailto:${company.email}`}>{company.email}</a>
            </span>
          </div>
        )}
        {company.phone && (
          <div className="info-item">
            <span className="info-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
            </span>
            <span className="info-text">
              <a href={`tel:${company.phone}`}>{company.phone}</a>
            </span>
          </div>
        )}
        {company.website && (
          <div className="info-item">
            <span className="info-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            </span>
            <span className="info-text">
              <a href={company.website} target="_blank" rel="noopener noreferrer">
                {company.website}
              </a>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const FormSection = ({ title, subtitle, children }) => (
  <fieldset className="form-section">
    <legend className="form-section-legend">
      <span className="form-section-title">{title}</span>
      {subtitle && <span className="form-section-subtitle">{subtitle}</span>}
    </legend>
    {children}
  </fieldset>
);

const DEFAULT_FORM_COPY = {
  headerLabel: 'Service request',
  formTitle: 'Raise a Request',
  formDescription: "Share your details and we'll follow up shortly.",
};

function ComplaintForm({ resolvedFormLink }) {
  const params = useParams();
  const companyId = resolvedFormLink ? String(resolvedFormLink.companyId) : params.companyId;
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const mapRef = useRef(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  // Preserve company for success view
  const [submittedCompany, setSubmittedCompany] = useState(null);
  
  // Add department state variables
  const [departmentName, setDepartmentName] = useState(resolvedFormLink ? resolvedFormLink.departmentName : '');
  const [isDepartmentVerified, setIsDepartmentVerified] = useState(!!resolvedFormLink);
  
  // Add iOS detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
              (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  const [formData, setFormData] = useState({
    companyId: companyId || '',
    departmentId: resolvedFormLink ? String(resolvedFormLink.departmentId) : '', // Store departmentId in form data
    name: '',
    email: '',
    phone: '',
    description: '',
    location: null,
    images: [],
    dueDate: ''
  });
  
  // Add these states for tracking uploads
  const [uploadProgress, setUploadProgress] = useState({});
  
  // Add a state to track if we're looking up a customer
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  
  // Add a new state for tracking the complaint reference
  const [complaintReference, setComplaintReference] = useState(null);
  
  // Add locationError state for location-specific errors
  const [locationError, setLocationError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const response = await api.get('/api/companies?isActive=true');
        if (response.data.is_success) {
          setCompanies(response.data.companies);
        }
      } catch (error) {
        console.error('Error fetching companies:', error);
        setError('Failed to load companies. Please try again later.');
      } finally {
        setLoadingCompanies(false);
      }
    };
    
    fetchCompanies();
  }, []);
  
  useEffect(() => {
    if (companyId) {
      setIsLoading(true);
      api.get(`/api/companies/${companyId}`)
        .then(response => {
          if (response.data.is_success) {
            const company = response.data.company;
            setCompanyName(company.name);
            setSelectedCompany(company);
            setFormData(prev => ({
              ...prev,
              companyId
            }));
          } else {
            setError('Company not found');
          }
        })
        .catch(error => {
          console.error('Error fetching company:', error);
          setError('Failed to load company information');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [companyId]);
  
  // Close suggestions dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Debounced function to search for places as user types
  const searchPlaces = useCallback(
    debounce(async (searchText) => {
      if (!searchText || searchText.length < 3) {
        setLocationSuggestions([]);
        return;
      }
      
      try {
        const response = await axios.get(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchText)}&limit=5`
        );
        
        if (response.data && Array.isArray(response.data)) {
          setLocationSuggestions(response.data);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Error fetching location suggestions:', error);
        setLocationSuggestions([]);
      }
    }, 500), // 500ms delay
    []
  );
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Add debugging to see what's happening with phone field
    if (name === 'phone') {
      console.log('Phone input changed:', value);
    }
    
    // If identifier fields are changing, reset existing customer state
    if (name === 'email' || name === 'phone') {
      setIsExistingCustomer(false);
    }
    
    // If customer is existing and field already has a value, prevent edits. Allow edits if missing.
    if (isExistingCustomer && (name === 'name' || name === 'phone') && (formData[name] && String(formData[name]).trim() !== '')) {
      return;
    }
    
    // Update form data, ensuring we never store undefined values
    setFormData(prevData => {
      const newData = {
        ...prevData,
        [name]: value
      };
      
      // Add debugging to see updated state
      if (name === 'phone') {
        console.log('Updated form state phone value:', newData.phone);
      }
      
      return newData;
    });
    
    // Handle company selection
    if (name === 'companyId') {
      const selected = companies.find(company => company.id.toString() === value);
      setSelectedCompany(selected || null);
    }
  };
  
  const updateAddressFromCoordinates = async (latlng) => {
    setIsLoadingLocation(true);
    try {
      const response = await api.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'Bl0omnav' // Required by Nominatim's usage policy
          }
        }
      );
      
      if (response.data && response.data.display_name) {
        setFormData(prev => ({
          ...prev,
          location: {
            ...prev.location,
            address: response.data.display_name,
            coordinates: { lat: latlng.lat, lng: latlng.lng }
          }
        }));
        setLocationError(''); // Clear any errors on successful address fetch
      }
    } catch (error) {
      console.error('Error fetching address:', error);
      setLocationError('Got your location but couldn\'t fetch the address. You can enter it manually.');
    } finally {
      setIsLoadingLocation(false);
    }
  };
  
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setLocationError(isIOS 
        ? 'Geolocation is not supported. Please update to a newer version of Safari or enter your address manually.'
        : 'Geolocation is not supported by your browser. Please enter your address manually.');
      return;
    }
    
    setIsLoadingLocation(true);
    setLocationError(''); // Clear any previous location errors
    
    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // 15 seconds timeout
      maximumAge: 300000 // 5 minutes cache
    };
    
    const success = async (position) => {
      try {
        const { latitude, longitude } = position.coords;
        
        // Update the coordinates
        setFormData(prev => ({
          ...prev,
          location: {
            ...prev.location,
            coordinates: { lat: latitude, lng: longitude }
          }
        }));
        
        // Update map position
        const newPosition = { lat: latitude, lng: longitude };
        setMapPosition(newPosition);
        
        // If map is rendered, fly to the new position
        if (mapRef.current) {
          mapRef.current.flyTo(newPosition, 15);
        }
        
        // Clear any previous location errors since we got coordinates
        setLocationError('');
        
        // Get address from coordinates using reverse geocoding
        try {
          const response = await api.get(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`, 
            {
              headers: {
                'User-Agent': 'Bl0omnav' // Required by Nominatim's usage policy
              }
            }
          );
          
          if (response.data && response.data.display_name) {
            setFormData(prev => ({
              ...prev,
              location: {
                ...prev.location,
                address: response.data.display_name,
                coordinates: { lat: latitude, lng: longitude }
              }
            }));
            setLocationError(''); // Clear any errors on successful address fetch
          }
        } catch (error) {
          console.error('Error fetching address:', error);
         // setLocationError('Got your location but couldn\'t fetch the address. You can enter it manually.');
        }
      } catch (error) {
        console.error('Error processing location:', error);
        setLocationError('Error processing your location. Please try again or enter your address manually.');
      } finally {
        setIsLoadingLocation(false);
      }
    };
    
    const error = (err) => {
      console.error('Geolocation error:', err);
      setIsLoadingLocation(false);
      
      let errorMessage = '';
      switch (err.code) {
        case err.PERMISSION_DENIED:
          errorMessage = isIOS 
            ? 'Location access denied. On iOS: Go to Settings > Privacy & Security > Location Services > Chrome/ Safari and enable location access. Then refresh this page and try again.'
            : 'Location access denied. Please enable location permissions in your browser settings and try again, or enter your address manually.';
          break;
        case err.POSITION_UNAVAILABLE:
          errorMessage = isIOS
            ? 'Location unavailable. Make sure Location Services is enabled in iOS Settings and you have a good connection. Try entering your address manually.'
            : 'Location information is unavailable. Please check your connection and try again, or enter your address manually.';
          break;
        case err.TIMEOUT:
          errorMessage = isIOS
            ? 'Location request timed out. This is common on iOS - try again or enter your address manually.'
            : 'Location request timed out. Please try again or enter your address manually.';
          break;
        default:
          errorMessage = isIOS
            ? 'Location error on iOS. Try enabling location in Settings > Privacy & Security > Location Services > Safari, or enter your address manually.'
            : 'An unknown error occurred while getting your location. Please enter your address manually.';
          break;
      }
      
      setLocationError(errorMessage);
    };
    
    // For iOS Safari, we need to call getCurrentPosition from a user gesture
    navigator.geolocation.getCurrentPosition(success, error, options);
  };
  
  const handleLocationInputChange = (e) => {
    const { value } = e.target;
    
    // Update the address in the form
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        address: value
      }
    }));
    
    // Clear location errors when user starts typing
    if (value && locationError) {
      setLocationError('');
    }
    
    // Search for places
    searchPlaces(value);
  };
  
  const handleSelectSuggestion = (suggestion) => {
    console.log('Selected location suggestion:', suggestion);

    // Update form with selected location
    setFormData(prev => ({
      ...prev,
      location: {
        address: suggestion.display_name,
        coordinates: {
          lat: parseFloat(suggestion.lat),
          lng: parseFloat(suggestion.lon)
        }
      }
    }));
    
    // Update map position
    const newPosition = {
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon)
    };
    setMapPosition(newPosition);
    
    // Fly to the selected location on the map
    if (mapRef.current) {
      mapRef.current.flyTo(newPosition, 15);
    }
    
    // Close suggestions dropdown and clear any location errors
    setShowSuggestions(false);
    setLocationSuggestions([]);
    setLocationError(''); // Clear location error on successful selection
  };
  
  // Add this function to detect and handle auto-filled location
  const handleLocationAutoFill = (e) => {
    const value = e.target.value;
    
    // If we suddenly have a non-empty value that wasn't explicitly typed by the user
    // it's likely from browser auto-fill
    if (value && !formData.location?.coordinates) {
      console.log('Detected potential auto-filled location:', value);
      
      // Update the form data with the address
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          address: value
        }
      }));
      
      // Search for places using the auto-filled value
      searchPlaces(value);
    }
  };
  
  // You may also want to automatically select the first suggestion if from auto-fill
  useEffect(() => {
    // If we have suggestions from an auto-fill event, select the first one
    if (locationSuggestions.length > 0 && formData.location?.address && !formData.location?.coordinates) {
      // Short delay to ensure the UI has updated with the suggestions
      const timer = setTimeout(() => {
        handleSelectSuggestion(locationSuggestions[0]);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [locationSuggestions]);
  const handleLocationFocus = () => {
    // If address exists but no coordinates, clear it to prevent using autofilled values without coordinates
    if (formData.location?.address && !formData.location?.coordinates) {
      console.log('Clearing address field on focus because it has no coordinates');
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          address: ''
        }
      }));
    }
    
    // Show suggestions if they exist
    if (locationSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };
  // Function to resize an image before upload
  const resizeImage = (file) => {
    return new Promise((resolve, reject) => {
      // Maximum dimensions for the resized image
      const MAX_WIDTH = 1024;
      const MAX_HEIGHT = 1024;
      const TARGET_FILE_SIZE_MB = 1;
      
      // Skip resizing if file is already small enough
      if (file.size <= TARGET_FILE_SIZE_MB * 1024 * 1024) {
        console.log(`Image already under ${TARGET_FILE_SIZE_MB}MB: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
        resolve(file);
        return;
      }
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round(height * (MAX_WIDTH / width));
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round(width * (MAX_HEIGHT / height));
              height = MAX_HEIGHT;
            }
          }
          
          // Create canvas and resize image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Get resized image as blob with lower quality
          canvas.toBlob(
            (blob) => {
              // Create new file from blob
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              
              console.log(`Resized image from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(resizedFile.size / 1024 / 1024).toFixed(2)}MB`);
              resolve(resizedFile);
            },
            file.type,
            0.7  // Lower quality (0.6 instead of 0.8) to target sub-1MB files
          );
        };
        
        img.onerror = (error) => {
          console.error('Error loading image for resizing:', error);
          resolve(file); // Use original file on error
        };
      };
      
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        resolve(file); // Use original file on error
      };
    });
  };
  
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImage(true);
    setError('');
    
    try {
      const uploadPromises = files.map(async (file, index) => {
        // Resize image before uploading
        const resizedFile = await resizeImage(file);
        
        // 1. Get presigned URL
        const response = await api.get('/api/upload/presigned-url', {
          params: {
            fileType: resizedFile.type
          }
        });
        
        const { uploadUrl, fileUrl } = response.data;
        
        // 2. Upload directly to S3 - simpler method
        const axiosInstance = axios.create();
        delete axiosInstance.defaults.headers.common['Authorization']; // Remove auth headers
        
        await axiosInstance.put(uploadUrl, resizedFile, {
          headers: {
            'Content-Type': resizedFile.type,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(prev => ({
              ...prev,
              [index]: percentCompleted
            }));
          }
        });
        
        return fileUrl;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      setError('Failed to upload images. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };
  
  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };
  
  useEffect(() => {
    // Check for departmentId in URL query params only if not using resolvedFormLink
    if (resolvedFormLink) {
      // departmentId is already set in initial state, so we don't need to read from URL
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const departmentId = params.get('departmentId');
    
    if (departmentId) {
      setFormData(prev => ({
        ...prev,
        departmentId
      }));
    }
  }, [resolvedFormLink]);
  
  // Verify department belongs to company when both are set
  useEffect(() => {
    const verifyDepartment = async () => {
      if (formData.companyId && formData.departmentId) {
        try {
          const response = await api.get(`/api/companies/${formData.companyId}/departments/${formData.departmentId}/verify`);
          if (response.data.is_success) {
            // If verification succeeds, set department name and verified status
            setDepartmentName(response.data.department.name);
            setIsDepartmentVerified(true);
          } else {
            // If verification fails, clear department
            setFormData(prev => ({
              ...prev,
              departmentId: ''
            }));
            setDepartmentName('');
            setIsDepartmentVerified(false);
            console.warn('Selected department does not belong to this company');
          }
        } catch (error) {
          console.error('Error verifying department:', error);
          // Clear department on error
          setFormData(prev => ({
            ...prev,
            departmentId: ''
          }));
          setDepartmentName('');
          setIsDepartmentVerified(false);
        }
      } else {
        // Reset department name and verification if no department ID
        setDepartmentName('');
        setIsDepartmentVerified(false);
      }
    };
    
    verifyDepartment();
  }, [formData.companyId, formData.departmentId]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeStep?.id !== 'attachments') {
      return;
    }
    
    // Log full form data before submission
    console.log('Full form data before submission:', formData);
    
    // Validate form data before submission
    if (!formData.name || !formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if ((!formData.email || !formData.email.trim()) && (!formData.phone || !String(formData.phone).trim())) {
      setError('Either email or phone number is required');
      return;
    }

    if (formData.phone && String(formData.phone).trim()) {
      const phoneDigits = String(formData.phone).replace(/\D/g, '');
      if (phoneDigits.length < 7 || phoneDigits.length > 15) {
        setError('Please enter a valid phone number');
        return;
      }
    }

    if (!formData.companyId) {
      setError('Please select a company');
      return;
    }
    
    if (!formData.description || !formData.description.trim()) {
      setError('Description is required');
      return;
    }
    if (!formData.location) {
      setError('Location is required');
      return;
    }

    // Due date is required
    if (!formData.dueDate) {
      setError('Due Date is required');
      return;
    }
    const dueDateValue = new Date(formData.dueDate);
    if (isNaN(dueDateValue.getTime())) {
      setError('Please provide a valid Due Date');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      // Create a clean copy of the customer data to avoid mutation issues
      const customerData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone, // Send as-is
        companyId: formData.companyId
      };
      
      // Log what we're actually sending
      console.log('Customer data being sent to API:', customerData);
      
      let customerResponse;
      if (!isExistingCustomer) {
        // Explicitly stringify the data to avoid any potential serialization issues
        const payload = JSON.stringify(customerData);
        
        console.log('Stringified payload:', payload);
        
        customerResponse = await api.post('/api/customers', { ...JSON.parse(payload), companyId: formData.companyId }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } else {
        // Get existing customer by email or phone
        const customerLookupParams = { companyId: formData.companyId };
        if (formData.email && formData.email.trim()) customerLookupParams.email = formData.email.trim();
        if (formData.phone && String(formData.phone).trim()) customerLookupParams.phone = String(formData.phone).trim();
        customerResponse = await api.get('/api/customers', {
          params: customerLookupParams
        });
      }
      
      if (!customerResponse.data.is_success) {
        throw new Error(customerResponse.data.message || 'Failed to process customer data');
      }
      
      const customerId = customerResponse.data.customer.id;
      
      // Include departmentId in submission only if it's verified
      const complaintData = {
        companyId: formData.companyId,
        customerId,
        departmentId: isDepartmentVerified ? formData.departmentId : null,
        description: formData.description.trim(),
        location: formData.location,
        images: formData.images || [],
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null
      };
      
      const complaintResponse = await api.post('/api/complaints/submit', complaintData);
      
      if (complaintResponse.data.is_success) {
        // Preserve company details for success header before resetting form
        setSubmittedCompany(selectedCompany);
        setIsSubmitted(true);
        setComplaintReference(complaintResponse.data.complaint.id);
        resetForm();
      } else {
        throw new Error(complaintResponse.data.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      setError(error.response?.data?.message || error.message || 'An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Reset form function
  const resetForm = () => {
    setFormData({
      companyId: '',
      departmentId: '',
      name: '',
      email: '',
      phone: '',
      description: '',
      location: null,
      images: [],
      dueDate: ''
    });
    setSelectedCompany(null);
    setDepartmentName('');
    setIsDepartmentVerified(false);
    setIsExistingCustomer(false);
  };
  
  // Handle company selection from dropdown
  const handleCompanySelect = async (e) => {
    const selectedCompanyId = e.target.value;
    
    if (selectedCompanyId) {
      try {
        // Fetch full company details
        const response = await api.get(`/api/companies/${selectedCompanyId}`);
        
        if (response.data.is_success) {
          const company = response.data.company;
          setCompanyName(company.name);
          setSelectedCompany(company);
          
          // Update form data
          setFormData(prev => ({
            ...prev,
            companyId: selectedCompanyId
          }));
        }
      } catch (error) {
        console.error('Error fetching company details:', error);
        setError('Failed to load company details');
      }
    } else {
      setCompanyName('');
      setSelectedCompany(null);
      
      // Clear company from form data
      setFormData(prev => ({
        ...prev,
        companyId: ''
      }));
    }
  };
  
  // Handle email field blur event
  const handleEmailBlur = async () => {
    // Get email value and ensure it's a string
    const email = formData.email ? formData.email.trim() : '';
    
    // Only proceed if we have a valid-looking email and company selected
    if (!email || !email.includes('@') || !formData.companyId) {
      return;
    }
    
    setIsCheckingEmail(true);
    
    try {
      const response = await api.get('/api/customers', {
        params: { email, companyId: formData.companyId }
      });
      
      if (response.data.is_success) {
        const customer = response.data.customer;
        
        // Update form with existing customer data
        setFormData(prev => ({
          ...prev,
          name: customer.name || '',
          phone: customer.phone || ''
        }));
        
        setIsExistingCustomer(true);
      } else {
        setIsExistingCustomer(false);
      }
    } catch (error) {
      // If 404, customer doesn't exist
      if (error.response?.status === 404) {
        setIsExistingCustomer(false);
      } else {
        console.error('Error checking customer email:', error);
      }
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handlePhoneBlur = async () => {
    const phone = formData.phone ? String(formData.phone).trim() : '';
    if (!phone || !formData.companyId) {
      return;
    }

    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      return;
    }

    setIsCheckingPhone(true);

    try {
      const response = await api.get('/api/customers', {
        params: { phone, companyId: formData.companyId }
      });

      if (response.data.is_success) {
        const customer = response.data.customer;
        setFormData(prev => ({
          ...prev,
          name: customer.name || prev.name || '',
          email: customer.email || prev.email || ''
        }));
        setIsExistingCustomer(true);
      } else {
        setIsExistingCustomer(false);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setIsExistingCustomer(false);
      } else {
        console.error('Error checking customer phone:', error);
      }
    } finally {
      setIsCheckingPhone(false);
    }
  };
  
  // Add this effect to log form data changes
  useEffect(() => {
    console.log('Form data updated:', formData);
    // Specifically log phone value
    console.log('Current phone value in state:', formData.phone);
  }, [formData]);

  // Mobile-friendly helpers for datetime-local (use local time, no seconds)
  const getLocalDateTimeInputValue = (date) => {
    const pad = (num) => String(num).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };
  // Min should be current time (seconds truncated), allow ANY minute selection
  const nowTruncated = (() => { const d = new Date(); d.setSeconds(0, 0); return d; })();
  const minDueDate = getLocalDateTimeInputValue(nowTruncated);
  
  const descriptionLength = (formData.description || '').length;

  const formSteps = useMemo(() => {
    const steps = [];
    if (!companyId) {
      steps.push({ id: 'organization', title: 'Organization', subtitle: 'Select your company' });
    }
    steps.push(
      { id: 'details', title: 'Details', subtitle: 'How we can reach you' },
      { id: 'request', title: 'Request', subtitle: 'What you need and when' },
      { id: 'location', title: 'Location', subtitle: 'Where the service is needed' },
      { id: 'attachments', title: 'Photos', subtitle: 'Optional photos' }
    );
    return steps;
  }, [companyId]);

  const totalSteps = formSteps.length;
  const activeStep = formSteps[currentStep] || formSteps[0];
  const isFirstStep = currentStep === 0;
  const isAttachmentsStep = activeStep?.id === 'attachments';

  useEffect(() => {
    setCurrentStep((prev) => Math.min(prev, Math.max(formSteps.length - 1, 0)));
  }, [formSteps.length]);

  const validateStep = (stepId) => {
    switch (stepId) {
      case 'organization':
        if (!formData.companyId) return 'Please select a company';
        return '';
      case 'details':
        if (!formData.name || !formData.name.trim()) return 'Name is required';
        if ((!formData.email || !formData.email.trim()) && (!formData.phone || !String(formData.phone).trim())) {
          return 'Either email or phone number is required';
        }
        if (formData.phone && String(formData.phone).trim()) {
          const phoneDigits = String(formData.phone).replace(/\D/g, '');
          if (phoneDigits.length < 7 || phoneDigits.length > 15) return 'Please enter a valid phone number';
        }
        return '';
      case 'request':
        if (!formData.description || !formData.description.trim()) return 'Description is required';
        if (!formData.dueDate) return 'Due Date is required';
        if (isNaN(new Date(formData.dueDate).getTime())) return 'Please provide a valid Due Date';
        return '';
      case 'location':
        if (!formData.location) return 'Location is required';
        if (!formData.location.address || !formData.location.address.trim()) return 'Address is required';
        return '';
      case 'attachments':
        return '';
      default:
        return '';
    }
  };

  const handleNextStep = () => {
    const stepError = validateStep(activeStep.id);
    if (stepError) {
      setError(stepError);
      return;
    }
    setError('');
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const handlePreviousStep = () => {
    setError('');
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!isAttachmentsStep) {
      handleNextStep();
      return;
    }

    handleSubmit(e);
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter' || isAttachmentsStep) return;

    const tag = e.target.tagName;
    if (tag === 'TEXTAREA') return;

    e.preventDefault();
    handleNextStep();
  };

  const formCopy = resolvedFormLink
    ? {
        headerLabel: resolvedFormLink.headerLabel ?? DEFAULT_FORM_COPY.headerLabel,
        formTitle: resolvedFormLink.formTitle ?? DEFAULT_FORM_COPY.formTitle,
        formDescription: resolvedFormLink.formDescription ?? DEFAULT_FORM_COPY.formDescription,
      }
    : DEFAULT_FORM_COPY;

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

    .page-container {
      --ink: #0a0a0a;
      --ink-soft: rgba(10, 10, 10, 0.62);
      --ink-faint: rgba(10, 10, 10, 0.38);
      --surface: #ffffff;
      --surface-muted: #f4f4f4;
      --line: #e2e2e2;
      --line-strong: #0a0a0a;

      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      background: var(--surface-muted);
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
      font-weight: 400;
      padding: 0.75rem clamp(0.75rem, 2vw, 1.25rem) 1.25rem;
      color: var(--ink);
      -webkit-font-smoothing: antialiased;
      box-sizing: border-box;
    }

    .page-container *,
    .page-container *::before,
    .page-container *::after {
      box-sizing: border-box;
    }

    .complaint-card {
      background: var(--surface);
      border: 1px solid var(--line);
      border-radius: 10px;
      padding: 1.25rem clamp(1rem, 2.5vw, 1.5rem) 1.35rem;
      width: 100%;
      max-width: 560px;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      gap: 1.125rem;
      margin-top: 0;
      text-align: left;
    }

    .header-section {
      text-align: left;
      width: 100%;
      padding-bottom: 1rem;
      border-bottom: 1px solid var(--line);
    }

    .form-eyebrow {
      display: block;
      width: 100%;
      font-size: 0.6875rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--ink-faint);
      margin-bottom: 0.35rem;
      text-align: left;
    }

    .page-container .complaint-card h1 {
      font-size: clamp(1.25rem, 2.5vw, 1.5rem);
      font-weight: 600;
      line-height: 1.25;
      margin: 0 0 0.35rem;
      letter-spacing: -0.02em;
      color: var(--ink);
      text-align: left;
      width: 100%;
    }

    .page-container .complaint-card .header-subtitle {
      font-size: 0.875rem;
      font-weight: 400;
      color: var(--ink-soft);
      margin: 0;
      line-height: 1.45;
      max-width: none;
      width: 100%;
      text-align: left;
    }

    .department-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      margin-top: 0.625rem;
      padding: 0.25rem 0.625rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--ink);
      background: var(--surface-muted);
      border: 1px solid var(--line);
      border-radius: 999px;
    }

    .company-header-card {
      margin-top: 0.875rem;
      padding: 0.75rem 0.875rem;
      text-align: left;
      width: 100%;
      background: var(--surface-muted);
      border: 1px solid var(--line);
      border-radius: 8px;
    }

    .company-header-card .company-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--ink);
      margin: 0 0 0.15rem;
    }

    .company-header-card .company-address {
      font-size: 0.8125rem;
      color: var(--ink-soft);
      line-height: 1.4;
      margin: 0;
    }

    .company-header-card .company-contacts {
      display: flex;
      flex-wrap: wrap;
      gap: 0.35rem 1rem;
      margin-top: 0.5rem;
      font-size: 0.75rem;
    }

    .company-header-card a {
      color: var(--ink);
      text-decoration: underline;
      text-underline-offset: 2px;
      text-decoration-color: var(--ink-faint);
      transition: text-decoration-color 0.15s;
    }

    .company-header-card a:hover {
      text-decoration-color: var(--ink);
    }

    .error-message {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;
      background: var(--surface-muted);
      color: var(--ink);
      padding: 0.75rem 0.875rem;
      border-radius: 8px;
      border: 1px solid var(--line-strong);
      font-size: 0.875rem;
      line-height: 1.45;
    }

    .error-message::before {
      content: '!';
      flex-shrink: 0;
      width: 1.125rem;
      height: 1.125rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 0.6875rem;
      background: var(--ink);
      color: var(--surface);
      border-radius: 50%;
    }

    .complaint-form {
      display: flex;
      flex-direction: column;
      gap: 0;
      width: 100%;
      max-width: 100%;
    }

    .step-progress {
      margin: 0;
      width: 100%;
      max-width: 100%;
    }

    .step-progress-meta {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 0.5rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--ink-soft);
    }

    .step-progress-count {
      white-space: nowrap;
      color: var(--ink-faint);
      font-weight: 400;
    }

    .step-progress-sr {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .step-track {
      height: 2px;
      background: var(--line);
      border-radius: 1px;
      overflow: hidden;
      margin-bottom: 0.875rem;
    }

    .step-track-fill {
      height: 100%;
      background: var(--ink);
      border-radius: 1px;
      transition: width 0.25s ease;
    }

    .step-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      grid-template-columns: repeat(var(--step-count, 4), minmax(0, 1fr));
      gap: 0.25rem;
      width: 100%;
      align-items: start;
    }

    .step-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: 0.375rem;
      min-width: 0;
      width: 100%;
      text-align: center;
    }

    .step-marker {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      border: 1.5px solid var(--line);
      border-radius: 50%;
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--ink-faint);
      background: var(--surface);
      flex-shrink: 0;
      line-height: 1;
    }

    .step-label {
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--ink-faint);
      line-height: 1.25;
      display: block;
      width: 100%;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .step-item.active .step-marker {
      border-color: var(--ink);
      background: var(--ink);
      color: var(--surface);
    }

    .step-item.active .step-label {
      color: var(--ink);
      font-weight: 600;
    }

    .step-item.completed .step-marker {
      border-color: var(--ink);
      color: var(--ink);
      background: var(--surface);
    }

    .step-item.completed .step-label {
      color: var(--ink-soft);
    }

    .step-panel {
      padding: 1rem 0 0.25rem;
      min-width: 0;
      width: 100%;
      max-width: 100%;
    }

    .step-panel .form-section-legend {
      display: none;
    }

    .step-panel-header {
      margin-bottom: 0.875rem;
      text-align: left;
    }

    .step-panel-title {
      font-size: 1rem;
      font-weight: 600;
      color: var(--ink);
      margin: 0 0 0.2rem;
    }

    .step-panel-subtitle {
      font-size: 0.8125rem;
      color: var(--ink-soft);
      margin: 0;
      line-height: 1.4;
      text-align: left;
    }

    .step-actions {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-top: 1.125rem;
      padding-top: 1rem;
      border-top: 1px solid var(--line);
      width: 100%;
    }

    .step-button {
      width: 100%;
      flex: none;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      font-size: 0.9375rem;
      font-weight: 500;
      cursor: pointer;
      transition: opacity 0.15s, background 0.15s;
      font-family: inherit;
      min-height: 2.75rem;
    }

    .step-button-back {
      background: var(--surface);
      color: var(--ink);
      border: 1px solid var(--line);
      order: 2;
    }

    .step-button-back:hover:not(:disabled) {
      background: var(--surface-muted);
      border-color: var(--ink-faint);
    }

    .step-button-next,
    .submit-button {
      background: var(--ink);
      color: var(--surface);
      border: 1px solid var(--ink);
      order: 1;
    }

    .step-button-next:hover:not(:disabled),
    .submit-button:hover:not(:disabled) {
      opacity: 0.88;
    }

    .step-button:disabled,
    .submit-button:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .form-section {
      border: none;
      margin: 0;
      padding: 0;
      min-width: 0;
      width: 100%;
      max-width: 100%;
      min-inline-size: 0;
    }

    .form-section-legend {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      padding: 0;
      margin: 0 0 1rem;
      width: 100%;
    }

    .form-section-title {
      font-size: 0.9375rem;
      font-weight: 600;
      letter-spacing: normal;
      text-transform: none;
      color: var(--ink);
    }

    .form-section-subtitle {
      font-size: 0.8125rem;
      font-weight: 400;
      color: var(--ink-soft);
      text-transform: none;
      letter-spacing: normal;
    }

    .complaint-form label {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--ink);
      margin-bottom: 0.3rem;
      display: block;
      text-align: left;
    }

    .complaint-form .required {
      color: var(--ink);
      margin-left: 0.1rem;
    }

    .complaint-form .form-group {
      margin-bottom: 0.875rem;
    }

    .complaint-form .form-group:last-child {
      margin-bottom: 0;
    }

    .complaint-form .form-row {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.875rem;
      width: 100%;
    }

    .complaint-form .select-wrapper,
    .complaint-form .location-input-container,
    .complaint-form .map-container,
    .complaint-form .image-upload-zone,
    .complaint-form .existing-customer-notice,
    .complaint-form .input-with-indicator {
      width: 100%;
      max-width: 100%;
    }

    .complaint-form input:not([type="file"]),
    .complaint-form textarea,
    .complaint-form select {
      width: 100%;
      background: var(--surface);
      border: 1px solid var(--line);
      border-radius: 8px;
      padding: 0.625rem 0.75rem;
      font-size: 0.9375rem;
      font-weight: 400;
      color: var(--ink);
      transition: border-color 0.15s, box-shadow 0.15s;
      font-family: inherit;
      min-height: 2.625rem;
    }

    .complaint-form textarea {
      resize: vertical;
      min-height: 6.5rem;
      line-height: 1.5;
    }

    .complaint-form input:not([type="file"]):focus,
    .complaint-form textarea:focus,
    .complaint-form select:focus {
      outline: none;
      border-color: var(--ink);
      box-shadow: 0 0 0 1px var(--ink);
    }

    .complaint-form input::placeholder,
    .complaint-form textarea::placeholder {
      color: var(--ink-faint);
    }

    .complaint-form .readonly-field {
      background: var(--surface-muted);
      color: var(--ink-soft);
      cursor: not-allowed;
      border-style: solid;
    }

    .field-note {
      display: block;
      margin-top: 0.3rem;
      font-size: 0.75rem;
      color: var(--ink-soft);
      line-height: 1.4;
      text-align: left;
    }

    .description-meta {
      display: flex;
      justify-content: flex-end;
      margin-top: 0.3rem;
      font-size: 0.75rem;
      color: var(--ink-faint);
    }

    .input-with-indicator {
      position: relative;
    }

    .input-with-indicator input {
      padding-right: 5.25rem;
    }

    .input-indicator {
      position: absolute;
      right: 0.625rem;
      top: 50%;
      transform: translateY(-50%);
      font-size: 0.6875rem;
      font-weight: 500;
      padding: 0.2rem 0.45rem;
      border-radius: 4px;
      pointer-events: none;
      letter-spacing: 0.01em;
    }

    .input-indicator.loading {
      color: var(--ink-soft);
      background: var(--surface-muted);
      border: 1px solid var(--line);
    }

    .input-indicator.success {
      color: var(--ink);
      background: var(--surface-muted);
      border: 1px solid var(--ink);
    }

    .select-loading {
      padding: 0.75rem;
      color: var(--ink-soft);
      font-size: 0.875rem;
      text-align: center;
      background: var(--surface-muted);
      border-radius: 8px;
      border: 1px dashed var(--line);
    }

    .existing-customer-notice {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;
      background: var(--surface-muted);
      border: 1px solid var(--line);
      border-radius: 8px;
      padding: 0.75rem 0.875rem;
      margin-bottom: 0.875rem;
      color: var(--ink);
      font-size: 0.875rem;
      line-height: 1.45;
      text-align: left;
    }

    .existing-customer-notice::before {
      content: '✓';
      flex-shrink: 0;
      width: 1.25rem;
      height: 1.25rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.625rem;
      font-weight: 600;
      background: var(--ink);
      border-radius: 50%;
      color: var(--surface);
    }

    .existing-customer-notice strong {
      font-weight: 600;
    }

    .location-input-container {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .location-autocomplete-wrapper {
      position: relative;
    }

    .location-suggestions {
      position: absolute;
      z-index: 1000;
      left: 0;
      right: 0;
      top: calc(100% + 2px);
      max-height: 220px;
      overflow-y: auto;
      background: var(--surface);
      border: 1px solid var(--line);
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    }

    .suggestion-item {
      padding: 0.6875rem 0.75rem;
      font-size: 0.8125rem;
      color: var(--ink);
      cursor: pointer;
      border-bottom: 1px solid var(--line);
      line-height: 1.45;
      transition: background 0.12s;
    }

    .suggestion-item:last-child {
      border-bottom: none;
    }

    .suggestion-item:hover {
      background: var(--surface-muted);
    }

    .location-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      background: var(--surface);
      border: 1px solid var(--line);
      color: var(--ink);
      border-radius: 8px;
      padding: 0.625rem 1rem;
      font-size: 0.8125rem;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.15s, border-color 0.15s;
      width: 100%;
      font-family: inherit;
      min-height: 2.625rem;
    }

    .location-button:hover:not(:disabled) {
      background: var(--surface-muted);
    }

    .location-button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .location-status {
      margin-top: 0.5rem;
    }

    .location-indicator {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8125rem;
      color: var(--ink-soft);
    }

    .location-spinner {
      width: 16px;
      height: 16px;
      border: 1.5px solid var(--line);
      border-top-color: var(--ink);
      border-radius: 50%;
      animation: complaint-spin 0.8s linear infinite;
      flex-shrink: 0;
    }

    .location-error-message {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      margin-top: 0.5rem;
      padding: 0.75rem 0.875rem;
      background: var(--surface-muted);
      border: 1px solid var(--line);
      border-radius: 8px;
      font-size: 0.8125rem;
      color: var(--ink);
      line-height: 1.45;
      text-align: left;
    }

    .location-error-message .error-icon {
      flex-shrink: 0;
      width: 1.125rem;
      height: 1.125rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 500;
      font-size: 0.6875rem;
      background: var(--ink);
      color: var(--surface);
      border-radius: 50%;
    }

    .map-container {
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid var(--line);
      margin-top: 0.75rem;
    }

    .map-placeholder {
      height: clamp(180px, 30vw, 240px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.625rem;
      background: var(--surface-muted);
      color: var(--ink-soft);
      font-size: 0.8125rem;
      text-align: center;
      padding: 1rem;
    }

    .map-placeholder.loading p {
      margin: 0;
    }

    .image-upload-zone {
      border: 1px dashed var(--ink-faint);
      border-radius: 8px;
      padding: 1.125rem 1rem;
      text-align: center;
      background: var(--surface-muted);
      transition: background 0.15s, border-color 0.15s;
    }

    .image-upload-zone:hover {
      background: #ececec;
      border-color: var(--ink);
    }

    .image-upload-hint {
      font-size: 0.75rem;
      color: var(--ink-soft);
      margin: 0.5rem 0 0;
    }

    .image-upload-container input[type="file"] {
      width: 100%;
      color: transparent;
      font-size: 0;
    }

    .image-upload-container input[type="file"]::file-selector-button {
      background: var(--ink);
      border: none;
      color: var(--surface);
      border-radius: 2px;
      padding: 0.5rem 1rem;
      margin-right: 0.75rem;
      cursor: pointer;
      font-size: 0.8125rem;
      font-weight: 400;
      font-family: inherit;
      transition: opacity 0.15s;
      min-height: 2.25rem;
    }

    .image-upload-container input[type="file"]::file-selector-button:hover {
      opacity: 0.82;
    }

    .upload-progress-container {
      margin-top: 0.75rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .upload-progress {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 0.75rem;
      color: var(--ink-soft);
    }

    .progress-bar {
      flex: 1;
      height: 3px;
      background: var(--line);
      border-radius: 1px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background: var(--ink);
      border-radius: 1px;
      transition: width 0.2s ease;
    }

    .image-preview-container {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-top: 0.75rem;
    }

    .image-preview {
      position: relative;
      width: 72px;
      height: 72px;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid var(--line);
    }

    .image-preview img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .remove-image-button {
      position: absolute;
      top: 3px;
      right: 3px;
      background: var(--ink);
      color: var(--surface);
      border: none;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      line-height: 1;
      cursor: pointer;
      transition: opacity 0.15s;
    }

    .remove-image-button:hover {
      opacity: 0.75;
    }

    .submit-button {
      margin-top: 0;
    }

    .local-footer {
      text-align: center;
      padding: 1.125rem 0 0.25rem;
      color: var(--ink-soft);
      font-size: 0.75rem;
      max-width: 560px;
      width: 100%;
    }

    .local-footer .footer-line {
      margin-bottom: 0.2rem;
    }

    .local-footer .footer-brand {
      color: var(--ink);
      text-decoration: underline;
      text-underline-offset: 2px;
      text-decoration-color: var(--ink-faint);
      font-weight: 400;
    }

    .local-footer .footer-brand:hover {
      text-decoration-color: var(--ink);
    }

    .local-footer .footer-subline {
      font-size: 0.6875rem;
      color: var(--ink-faint);
      margin-top: 0.125rem;
    }

    .bloomnav-marker { position: relative; width: 30px; height: 30px; }
    .bloomnav-marker .bm-dot { position: absolute; left: 50%; top: 50%; width: 14px; height: 14px; background: #0a0a0a; border: 2px solid #fff; border-radius: 9999px; box-shadow: 0 1px 4px rgba(0,0,0,0.3); transform: translate(-50%, -50%); }
    .bloomnav-marker .bm-pulse { position: absolute; left: 50%; top: 50%; width: 14px; height: 14px; border-radius: 9999px; background: rgba(10,10,10,0.25); transform: translate(-50%, -50%); animation: bm-pulse 2s ease-out infinite; }
    @keyframes bm-pulse { 0% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 100% { transform: translate(-50%, -50%) scale(3); opacity: 0; } }

    .leaflet-container { background: #f4f4f4; }
    .leaflet-popup-content-wrapper, .leaflet-popup-tip {
      background: #ffffff;
      color: #0a0a0a;
      border-radius: 2px;
      border: 1px solid #e2e2e2;
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    }

    .complaint-loader {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.875rem;
      min-height: 200px;
    }

    .complaint-loader-spinner {
      width: 28px;
      height: 28px;
      border: 2px solid var(--line);
      border-top-color: var(--ink);
      border-radius: 50%;
      animation: complaint-spin 0.85s linear infinite;
    }

    .complaint-loader-text {
      font-size: 0.875rem;
      color: var(--ink-soft);
    }

    @keyframes complaint-spin { to { transform: rotate(360deg); } }

    .success-icon-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      margin: 0.5rem 0 1.25rem;
    }

    .success-icon {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: var(--surface-muted);
      border: 1px solid var(--ink);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--ink);
      animation: success-pop 0.4s cubic-bezier(0.34, 1.4, 0.64, 1);
    }

    @keyframes success-pop {
      0% { transform: scale(0.6); opacity: 0; }
      100% { transform: scale(1); opacity: 1; }
    }

    .success-reference {
      margin: 0;
      padding: 0;
      background: none;
      border: none;
      font-size: 0.8125rem;
      color: var(--ink-soft);
      text-align: center;
      line-height: 1.4;
    }

    .success-reference strong {
      display: block;
      margin-top: 0.2rem;
      color: var(--ink);
      font-weight: 500;
      font-size: 1.0625rem;
      letter-spacing: 0.01em;
    }

    .success-message { padding: 0; }
    .success-content { text-align: center; max-width: 100%; }
    .success-content h3 {
      font-size: 1.25rem;
      font-weight: 500;
      margin: 0 0 0.625rem;
      color: var(--ink);
      letter-spacing: -0.01em;
    }

    .success-body {
      font-size: 0.9375rem;
      color: var(--ink-soft);
      line-height: 1.55;
      margin: 0;
    }

    .success-header {
      text-align: center;
      padding-bottom: 1rem;
      margin-bottom: 0.25rem;
      border-bottom: 1px solid var(--line);
    }

    .success-company-name {
      font-size: 1.125rem;
      font-weight: 500;
      color: var(--ink);
      margin: 0 0 0.3rem;
    }

    .success-meta {
      font-size: 0.8125rem;
      color: var(--ink-soft);
      line-height: 1.5;
      word-break: break-word;
    }

    .success-meta a {
      color: var(--ink);
      text-decoration: underline;
      text-underline-offset: 2px;
      text-decoration-color: var(--ink-faint);
    }

    .success-meta a:hover { text-decoration-color: var(--ink); }
    .success-contact .contact-line { margin-top: 0.3rem; }

    .company-details-card { padding: 1rem; background: var(--surface-muted); border-radius: 2px; border: 1px solid var(--line); }
    .company-details-card .company-name { margin: 0 0 0.625rem; font-size: 0.9375rem; font-weight: 500; color: var(--ink); }
    .company-info-container { display: flex; flex-direction: column; gap: 0.5rem; }
    .info-item { display: flex; align-items: flex-start; gap: 0.6rem; font-size: 0.8125rem; color: var(--ink-soft); }
    .info-icon { flex-shrink: 0; color: var(--ink); margin-top: 0.1rem; display: flex; opacity: 0.7; }
    .info-text a { color: var(--ink); text-decoration: underline; text-underline-offset: 2px; text-decoration-color: var(--ink-faint); }
    .info-text a:hover { text-decoration-color: var(--ink); }

    @media (min-width: 520px) {
      .complaint-form .form-row {
        grid-template-columns: 1fr 1fr;
        gap: 0.875rem;
      }
    }

    @media (min-width: 768px) {
      .step-actions {
        flex-direction: row-reverse;
        align-items: center;
      }

      .step-button,
      .submit-button {
        flex: 1 1 0;
        width: auto;
        min-width: 0;
        order: 0;
      }

      .step-button-back {
        flex: 0 0 auto;
        min-width: 7.5rem;
      }

      .map-container .leaflet-container,
      .map-container > div[style*="height"] {
        height: 300px !important;
      }
    }

    @media (max-width: 640px) {
      .complaint-card {
        padding: 1rem 0.875rem 1.125rem;
        border-left: none;
        border-right: none;
        border-radius: 0;
        max-width: 100%;
      }

      .page-container {
        padding: 0 0 0.75rem;
        background: var(--surface);
      }

      .header-section {
        padding-bottom: 0.875rem;
      }

      .step-label {
        font-size: 0.625rem;
      }

      .step-actions {
        position: sticky;
        bottom: 0;
        z-index: 10;
        background: var(--surface);
        margin-top: 0.875rem;
        padding-top: 0.875rem;
        padding-bottom: max(0.25rem, env(safe-area-inset-bottom));
        box-shadow: 0 -8px 16px rgba(255, 255, 255, 0.92);
        flex-direction: column;
      }

      .step-button,
      .submit-button {
        width: 100%;
        flex: none;
        order: 0;
      }

      .step-button-back {
        order: 2;
      }

      .step-button-next,
      .submit-button {
        order: 1;
      }

      .image-preview {
        width: 64px;
        height: 64px;
      }
    }
  `;

  if (isLoading && !isSubmitted) {
    return (
      <div className="page-container">
        <style>{globalStyles}</style>
        <section className="complaint-card" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="complaint-loader" role="status" aria-live="polite">
            <span className="complaint-loader-spinner" aria-hidden="true"></span>
            <span className="complaint-loader-text">Loading...</span>
          </div>
        </section>
      </div>
    );
  }
  
  if (isSubmitted) {
    return (
      <div className="page-container">
        <style>{globalStyles}</style>
        <section className="complaint-card">
          {submittedCompany && (
            <div className="success-header">
              <div className="success-company-name">{submittedCompany.name}</div>
              {submittedCompany.address?.address && (
                <div className="success-meta">{submittedCompany.address.address}</div>
              )}
              {(submittedCompany.email || submittedCompany.phone || submittedCompany.website) && (
                <div className="success-meta success-contact" aria-label="Company contact">
                  {submittedCompany.email && (
                    <div className="contact-line"><a href={`mailto:${submittedCompany.email}`}>{submittedCompany.email}</a></div>
                  )}
                  {submittedCompany.phone && (
                    <div className="contact-line"><a href={`tel:${submittedCompany.phone}`}>{submittedCompany.phone}</a></div>
                  )}
                  {submittedCompany.website && (
                    <div className="contact-line"><a href={submittedCompany.website} target="_blank" rel="noopener noreferrer">{submittedCompany.website}</a></div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="success-icon-wrap">
            <div className="success-icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
            </div>
            {complaintReference && (
              <p className="success-reference">
                Reference
                <strong>#{complaintReference}</strong>
              </p>
            )}
          </div>
          <div className="success-message">
            <div className="success-content">
              <h3>Request submitted</h3>
              <p className="success-body">
                We&apos;ve received your request and will get back to you as soon as possible.
              </p>
            </div>
          </div>
        </section>
        <footer className="local-footer">
          <div className="footer-line">Powered by <a href="/" className="footer-brand">BloomNav</a></div>
          <div className="footer-subline">Complete business management solution</div>
          <div className="footer-subline">Built in Goa</div>
        </footer>
      </div>
    );
  }
  
  return (
    <div className="page-container">
      <style>{globalStyles}</style>
      <section className="complaint-card">
        <div className="header-section">
          <span className="form-eyebrow">{formCopy.headerLabel}</span>
          <h1>{formCopy.formTitle}</h1>
          <p className="header-subtitle">{formCopy.formDescription}</p>
          {selectedCompany && (
            <div className="company-header-card">
              <p className="company-title">{selectedCompany.name}</p>
              {selectedCompany.address && (
                <p className="company-address">{selectedCompany.address.address}</p>
              )}
              {(selectedCompany.email || selectedCompany.phone) && (
                <div className="company-contacts">
                  {selectedCompany.email && (
                    <a href={`mailto:${selectedCompany.email}`}>{selectedCompany.email}</a>
                  )}
                  {selectedCompany.phone && (
                    <a href={`tel:${selectedCompany.phone}`}>{selectedCompany.phone}</a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {error && <div className="error-message" role="alert">{error}</div>}

      <form onSubmit={handleFormSubmit} onKeyDown={handleFormKeyDown} className="complaint-form" noValidate>
        <nav
          className="step-progress"
          aria-label="Form progress"
          style={{ '--step-count': totalSteps }}
        >
          <div className="step-track" aria-hidden="true">
            <div
              className="step-track-fill"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
          <ol className="step-list">
            {formSteps.map((step, index) => (
              <li
                key={step.id}
                className={`step-item${index === currentStep ? ' active' : ''}${index < currentStep ? ' completed' : ''}`}
                aria-current={index === currentStep ? 'step' : undefined}
              >
                <span className="step-marker" aria-hidden="true">{index + 1}</span>
                <span className="step-label">{step.title}</span>
              </li>
            ))}
          </ol>
          <p className="step-progress-sr" aria-live="polite">
            Step {currentStep + 1} of {totalSteps}: {activeStep.title}
          </p>
        </nav>

        <div className="step-panel">
          <div className="step-panel-header">
            <h2 className="step-panel-title">{activeStep.title}</h2>
            <p className="step-panel-subtitle">{activeStep.subtitle}</p>
          </div>

          {activeStep.id === 'organization' && (
            <FormSection title="Organization">
              <div className="form-group">
                <label htmlFor="companySelect">Company</label>
                <div className="select-wrapper">
                  {loadingCompanies ? (
                    <div className="select-loading">Loading companies...</div>
                  ) : (
                    <select
                      id="companySelect"
                      value={formData.companyId}
                      onChange={handleCompanySelect}
                    >
                      <option value="">Select a company</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </FormSection>
          )}

          {activeStep.id === 'details' && (
            <>
              {isExistingCustomer && (
                <div className="existing-customer-notice" role="status">
                  <div className="notice-text">
                    <strong>Welcome back!</strong> We&apos;ve filled in your details from a previous submission.
                  </div>
                </div>
              )}

              <FormSection title="Your details">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <div className="input-with-indicator">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      onBlur={handleEmailBlur}
                      placeholder="you@example.com"
                    />
                    {isCheckingEmail && <div className="input-indicator loading">Checking</div>}
                    {isExistingCustomer && !isCheckingEmail && (
                      <div className="input-indicator success">Found</div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleInputChange}
                      placeholder="Full name"
                      readOnly={isExistingCustomer && Boolean(formData.name)}
                      className={isExistingCustomer && Boolean(formData.name) ? 'readonly-field' : ''}
                    />
                    {isExistingCustomer && Boolean(formData.name) && (
                      <small className="field-note">From your saved profile</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <div className="input-with-indicator">
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                        onBlur={handlePhoneBlur}
                        placeholder="+91 98765 43210"
                        readOnly={isExistingCustomer && Boolean(formData.phone)}
                        className={isExistingCustomer && Boolean(formData.phone) ? 'readonly-field' : ''}
                        inputMode="tel"
                        autoComplete="tel"
                      />
                      {isCheckingPhone && <div className="input-indicator loading">Checking</div>}
                    </div>
                  </div>
                </div>
              </FormSection>
            </>
          )}

          {activeStep.id === 'request' && (
            <FormSection title="Request">
              <div className="form-group">
                <label htmlFor="description">
                  Description <span className="required">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="5"
                  placeholder="Describe your request in as much detail as you can..."
                />
                <div className="description-meta" aria-live="polite">
                  {descriptionLength} characters
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="dueDate">
                  Preferred date &amp; time <span className="required">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="dueDate"
                  name="dueDate"
                  value={formData.dueDate || ''}
                  onChange={handleInputChange}
                  min={minDueDate}
                  step="any"
                  inputMode="numeric"
                />
                <small className="field-note">Schedule when you&apos;d like us to meet or follow up</small>
              </div>
            </FormSection>
          )}

          {activeStep.id === 'location' && (
            <FormSection title="Location">
              <div className="form-group">
                <label htmlFor="address">Address <span className="required">*</span></label>

                <div className="location-input-container">
                  <div className="location-autocomplete-wrapper" ref={suggestionsRef}>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.location?.address || ''}
                      onChange={handleLocationInputChange}
                      onFocus={handleLocationFocus}
                      onAnimationStart={handleLocationAutoFill}
                      onInput={handleLocationAutoFill}
                      placeholder="Start typing to search for locations"
                      disabled={isLoadingLocation}
                      autoComplete="new-password"
                      data-lpignore="true"
                    />

                    {showSuggestions && locationSuggestions.length > 0 && (
                      <div className="location-suggestions">
                        {locationSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            className="suggestion-item"
                            onClick={() => handleSelectSuggestion(suggestion)}
                          >
                            {suggestion.display_name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="location-button"
                    onClick={handleGetLocation}
                    disabled={isLoadingLocation}
                    title={isIOS ? "Note: Make sure location is enabled in iOS Settings > Privacy & Security > Location Services > Safari" : "Get your current location"}
                  >
                    {isLoadingLocation ? (
                      <span className="location-spinner"></span>
                    ) : (
                      isIOS ? 'Use my location (iOS)' : 'Use my current location'
                    )}
                  </button>
                </div>

                {isLoadingLocation && (
                  <div className="location-status">
                    <div className="location-indicator">
                      <div className="location-spinner"></div>
                      <span>Getting your location...</span>
                    </div>
                  </div>
                )}

                {locationError && !isLoadingLocation && (
                  <div className="location-error-message">
                    <div className="error-icon">!</div>
                    <div className="error-text">{locationError}</div>
                  </div>
                )}

                <div className="map-container" style={{ position: 'relative' }}>
                  {(mapPosition || formData.location?.coordinates) && (
                    <MapContainer
                      center={mapPosition || [formData.location?.coordinates?.lat || 0, formData.location?.coordinates?.lng || 0]}
                      zoom={15}
                      scrollWheelZoom={false}
                      style={{ height: 'clamp(220px, 40vw, 320px)', width: '100%' }}
                      whenCreated={mapInstance => { mapRef.current = mapInstance; }}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <LocationMarker
                        position={mapPosition || (formData.location?.coordinates ?
                          { lat: formData.location.coordinates.lat, lng: formData.location.coordinates.lng } : null)}
                        setPosition={setMapPosition}
                        updateAddress={updateAddressFromCoordinates}
                      />
                    </MapContainer>
                  )}

                  {isLoadingLocation && !mapPosition && !formData.location?.coordinates && (
                    <div className="map-placeholder loading">
                      <div className="location-spinner"></div>
                      <p>Loading map...</p>
                    </div>
                  )}

                  {!isLoadingLocation && !mapPosition && !formData.location?.coordinates && (
                    <div className="map-placeholder">
                      <p>Search for an address or use your current location</p>
                    </div>
                  )}
                </div>
              </div>
            </FormSection>
          )}

          {activeStep.id === 'attachments' && (
            <FormSection title="Attachments">
              <div className="form-group">
                <div className="image-upload-zone">
                  <div className="image-upload-container">
                    <input
                      type="file"
                      id="images"
                      name="images"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                    <p className="image-upload-hint">PNG, JPG up to a 5 MB each</p>
                  </div>
                  {uploadingImage && (
                    <div className="upload-progress-container">
                      {Object.keys(uploadProgress).map((fileIndex) => (
                        <div key={fileIndex} className="upload-progress">
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${uploadProgress[fileIndex]}%` }}
                            />
                          </div>
                          <span>{uploadProgress[fileIndex]}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {formData.images.length > 0 && (
                  <div className="image-preview-container">
                    {formData.images.map((url, index) => (
                      <div key={index} className="image-preview">
                        <img src={url} alt={`Attachment ${index + 1}`} />
                        <button
                          type="button"
                          className="remove-image-button"
                          onClick={() => removeImage(index)}
                          aria-label={`Remove image ${index + 1}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FormSection>
          )}
        </div>

        <div className="step-actions">
          {!isFirstStep && (
            <button
              type="button"
              className="step-button step-button-back"
              onClick={handlePreviousStep}
              disabled={isLoading || uploadingImage}
            >
              Back
            </button>
          )}
          <button
            type="submit"
            className={`step-button ${isAttachmentsStep ? 'submit-button' : 'step-button-next'}`}
            disabled={isLoading || uploadingImage}
          >
            {isAttachmentsStep
              ? (isLoading ? 'Submitting…' : 'Submit request')
              : 'Continue'}
          </button>
        </div>
      </form>
      </section>
      <footer className="local-footer">
        <div className="footer-line">Powered by <Link to="/" className="footer-brand">BloomNav</Link></div>
        <div className="footer-subline">Complete business management solution</div>
        <div className="footer-subline">Built in Goa</div>
      </footer>
    </div>
  );
}

export default ComplaintForm; 